/**
 * @takeover/sdk configuration for the takeover-app frontend.
 *
 * The SDK config is the contract between this app and the SDK's hooks
 * (`useGrid`, `useSeats`, `useGlobalActivity`, …). It tells the SDK:
 *
 *   - which API gateway to hit (`apiBaseUrl`)
 *   - which chain to read from (`chainId`)
 *   - which RPC URL to use for direct onchain reads (`rpcUrl`)
 *   - which grids this app cares about (`grids`)
 *   - which Privy app to share sessions with (`privyAppId`)
 *
 * For the Takeover production app the gateway is the same Next.js server
 * that hosts the SDK (i.e. our own `/api/v1/*` routes), so `apiBaseUrl`
 * resolves to the current origin. In SSR contexts where `window` is
 * unavailable we fall back to `''` and let the SDK's `fetch` calls hit
 * relative paths.
 *
 * `grids` is intentionally seeded with just the Boardroom (the protocol's
 * canonical hero grid). Curated/community boards are surfaced through the
 * boards-lobby endpoints in Phase 2 and are *not* part of the static
 * config.
 */

import { createTakeoverSDK, type TakeoverSDKConfig, type GridRef } from '@takeover/sdk';

const HV_UGM_ADDRESS = process.env.NEXT_PUBLIC_HV_UGM_ADDRESS;
const HV_GRID_ID = process.env.NEXT_PUBLIC_HV_GRID_ID ?? '0';
const PRIVY_APP_ID = process.env.NEXT_PUBLIC_PRIVY_APP_ID;
const RPC_URL = process.env.NEXT_PUBLIC_RPC_URL;
const APP_URL = process.env.NEXT_PUBLIC_APP_URL;
const CHAIN_ID = parseInt(process.env.NEXT_PUBLIC_CHAIN_ID ?? '8453', 10);

function resolveApiBaseUrl(): string {
  if (APP_URL && APP_URL.length > 0) return APP_URL.replace(/\/$/, '');
  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin;
  }
  // SSR fallback. The SDK rejects an empty `apiBaseUrl`, so we synthesize
  // one from the deployment's public origin. Mirrors the fallback chain
  // in `getInternalAppBaseUrl` and `app/layout.tsx#resolveMetadataBaseHref`
  // so a Railway/Vercel build (where `NEXT_PUBLIC_APP_URL` may not be
  // set explicitly but the platform exposes `RAILWAY_PUBLIC_DOMAIN` /
  // `VERCEL_URL`) can prerender static pages without the SDK config
  // throwing during `next build`. Without this, every page that imports
  // a hook touching `getTakeoverSDKConfig()` (every lobby surface) blew
  // up the production export step. In real `localhost` dev (no platform
  // vars set) we fall through to the dev-server port. In production the
  // final branch fails closed so a misconfigured deploy throws at boot
  // rather than baking `http://localhost:3000` into the bundle.
  if (process.env.RAILWAY_PUBLIC_DOMAIN) {
    return `https://${process.env.RAILWAY_PUBLIC_DOMAIN}`.replace(/\/$/, '');
  }
  if (process.env.VERCEL_URL) {
    return `https://${process.env.VERCEL_URL}`.replace(/\/$/, '');
  }
  if (process.env.NEXT_PUBLIC_APP_MODE === 'production') {
    throw new Error(
      '[takeover-sdk] NEXT_PUBLIC_APP_URL (or RAILWAY_PUBLIC_DOMAIN / VERCEL_URL) must be set when NEXT_PUBLIC_APP_MODE=production (no window.location available during SSR).',
    );
  }
  const port = process.env.PORT ?? '3000';
  return `http://localhost:${port}`;
}

function resolveSeedGrids(): GridRef[] {
  // Boardroom is the only statically-known grid. All other curated /
  // community boards come from `/api/v1/grids` at runtime and don't need
  // to be hard-coded into the SDK config.
  if (!HV_UGM_ADDRESS) return [];
  return [
    {
      ugmAddress: HV_UGM_ADDRESS,
      gridId: HV_GRID_ID,
      label: 'Boardroom',
    },
  ];
}

let cachedConfig: TakeoverSDKConfig | null = null;

export function getTakeoverSDKConfig(): TakeoverSDKConfig {
  if (cachedConfig) return cachedConfig;

  const grids = resolveSeedGrids();
  // `createTakeoverSDK` requires at least one grid. During very early
  // boot (no env vars at all, e.g. unit-test SSR) we keep a permissive
  // shim so the provider never throws. Real callers are gated by the
  // env wiring in `.env.local` / Railway.
  if (grids.length === 0) {
    cachedConfig = {
      apiBaseUrl: resolveApiBaseUrl(),
      chainId: CHAIN_ID,
      rpcUrl: RPC_URL,
      grids: [],
      privyAppId: PRIVY_APP_ID,
      chatBackendUrl: resolveApiBaseUrl(),
    } as TakeoverSDKConfig;
    return cachedConfig;
  }

  cachedConfig = createTakeoverSDK({
    apiBaseUrl: resolveApiBaseUrl(),
    chainId: CHAIN_ID,
    rpcUrl: RPC_URL,
    grids,
    privyAppId: PRIVY_APP_ID,
  });
  return cachedConfig;
}
